import React, { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Loader2,
  BarChart2,
  AlertTriangle,
  UserMinus,
  Info,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "src/components/ui/card";
import { Alert, AlertDescription } from "src/components/ui/alert";
import { Badge } from "src/components/ui/badge";
import { Switch } from "src/components/ui/switch";
import { Label } from "src/components/ui/label";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/components/ui/hover-card";

const SCORE_DESCRIPTIONS = {
  5: "Absolútne súhlasím / s riešením sa plne stotožňujem",
  4: "Súhlasím / s riešením súhlasím s miernymi výhradami",
  3: "Skôr súhlasím / s riešením súhlasím s viacerými výhradami",
  2: "Skôr nesúhlasím / riešenie obsahuje prvky, pre ktoré s ním nesúhlasím",
  1: "Nesúhlasím / situáciu by som riešil úplne inak",
  0: "Absolútne nesúhlasím / riešenie je nevhodné (po odbornej stránke, v rozpore s pravidlami, s firemnou kultúrou a pod.)",
};

const COLORS = {
  score5: "#22c55e", // Zelená pre 5 hviezd - absolútny súhlas
  score4: "#eab308", // Žltá pre 4 hviezdy - súhlas s výhradami
  score3: "#eab308", // Žltá pre 3 hviezdy - súhlas s výhradami
  score2: "#ef4444", // Červená pre 2 hviezdy - nesúhlas
  score1: "#ef4444", // Červená pre 1 hviezdu - nesúhlas
  score0: "#1f2937", // Čierna pre 0 hviezd - absolútny nesúhlas
};

const SelfAssessmentChange = ({
  evaluationData,
  loading = false,
  error = null,
  isGroupComponent = false,
}) => {
  const [excludeSelfAssessment, setExcludeSelfAssessment] = useState(false);

  // Process data from the parent component
  const { chartData, userCount } = useMemo(() => {
    // Initialize with default empty values
    const defaultResult = {
      chartData: [],
      userCount: 0,
    };

    // Return early if no data available
    if (
      !evaluationData ||
      !evaluationData.solutions ||
      evaluationData.solutions.length === 0
    ) {
      return defaultResult;
    }

    // Process the data for visualization
    const processedData = evaluationData.solutions
      .filter((solution) => solution.user_id) // Only include solutions with user_id
      .map((solution) => {
        // Initialize scores object with zeros
        const scores = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

        // Get evaluations, filtering out self-assessments if needed
        const relevantEvaluations = excludeSelfAssessment
          ? solution.evaluations.filter(
              (ev) => ev.evaluator_id !== solution.user_id
            )
          : solution.evaluations;

        // Count the frequency of each score
        relevantEvaluations.forEach((eval_) => {
          if (eval_.score !== undefined) {
            scores[eval_.score] = (scores[eval_.score] || 0) + 1;
          }
        });

        return {
          name: solution.users
            ? `${solution.users.first_name} ${solution.users.last_name}`
            : "Unknown",
          ...scores, // This spreads out the score counts
        };
      });

    return {
      chartData: processedData,
      userCount: processedData.length,
    };
  }, [evaluationData, excludeSelfAssessment]);

  const getStarGrammar = (count) => {
    const starCount = parseInt(count);
    switch (starCount) {
      case 1:
        return "hviezda";
      case 2:
      case 3:
      case 4:
        return "hviezdy";
      case 5:
        return "hviezd";
      default:
        return "hviezd";
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div className="bg-white p-4 border rounded-lg shadow-lg">
        <h3 className="font-medium text-gray-900 mb-2">{label}</h3>
        <div className="space-y-2">
          {payload.map((entry, index) => (
            <div key={index} className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm text-gray-600">
                    {`${entry.dataKey} ${getStarGrammar(entry.dataKey)}`}
                  </span>
                </div>
                <span className="font-medium">{`${entry.value} hodnotení`}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Handle the case when no data is available
  const noData = !chartData.length;

  const emptyStateContent = (
    <div className="flex justify-center items-center h-64 bg-gray-50 rounded-lg">
      <p className="text-gray-500">
        Žiadne dáta o hodnoteniach nie sú dostupné
      </p>
    </div>
  );

  if (noData) {
    return (
      <div className="flex justify-center items-center h-64 bg-gray-50 rounded-lg">
        <p className="text-gray-500">
          Žiadne dáta o hodnoteniach nie sú dostupné
        </p>
      </div>
    );
  }

  const content = (
    <div className="h-[400px] overflow-x-auto">
      <div className="min-w-[500px] h-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              interval={0}
              height={70}
              tick={{
                fontSize: 11,
                dx: -8,
              }}
              tickFormatter={(value) => {
                return value.length > 12
                  ? value.substring(0, 12) + "..."
                  : value;
              }}
            />
            <YAxis
              label={{
                value: "Počet hodnotení",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 12 },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {[5, 4, 3, 2, 1, 0].map((score) => (
              <Bar
                key={score}
                dataKey={score.toString()}
                name={`${score} ${getStarGrammar(score)}`}
                fill={COLORS[`score${score}`]}
                stackId="stack"
                barSize={70}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  if (isGroupComponent) {
    return content;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center space-x-2">
          <BarChart2 className="h-5 w-5" />
          <CardTitle>Distribúcia hodnotení</CardTitle>
          <HoverCard>
            <HoverCardTrigger asChild>
              <Info className="h-4 w-4 text-gray-500 cursor-help" />
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              <div className="space-y-2">
                <h4 className="font-semibold">Vysvetlenie bodového systému:</h4>
                {Object.entries(SCORE_DESCRIPTIONS).map(
                  ([score, description]) => (
                    <p key={score} className="text-sm text-gray-500">
                      <strong>{score} bodov:</strong> {description}
                    </p>
                  )
                )}
              </div>
            </HoverCardContent>
          </HoverCard>
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center mt-3 gap-3">
          <div className="flex items-center space-x-2 bg-gray-50 p-1.5 px-3 rounded-full">
            <Switch
              id="exclude-self-assessment-chart"
              checked={excludeSelfAssessment}
              onCheckedChange={setExcludeSelfAssessment}
              className="data-[state=checked]:bg-blue-600"
            />
            <Label
              htmlFor="exclude-self-assessment-chart"
              className="cursor-pointer flex items-center text-sm font-medium"
            >
              <UserMinus className="h-3.5 w-3.5 mr-1.5" />
              Bez sebahodnotenia
            </Label>
          </div>

          <Badge variant="secondary" className="bg-blue-50 text-blue-700">
            {userCount} hodnotiteľov
          </Badge>
        </div>
      </CardHeader>

      <CardContent>{content}</CardContent>
    </Card>
  );
};

export default SelfAssessmentChange;
